import { UserRoundPlusIcon, UsersRoundIcon } from "lucide-react";
import type { ReactNode } from "react";
import type { TicketAgentRefDto } from "@/api/tickets";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useI18n } from "@/locales/i18n-provider";
import { cn } from "@/utils";
import { initialsOf } from "@/utils/person-name";

type TicketFollowUpAssignmentProps = {
  assignee: TicketAgentRefDto | null;
  teamName: string | null;
};

/** 将客服组和负责人合并为一组有层级的跟进关系，并自然收缩空状态。 */
export function TicketFollowUpAssignment({ assignee, teamName }: TicketFollowUpAssignmentProps) {
  const { t } = useI18n();
  const assignedTeamName = teamName?.trim() || null;

  if (!assignee && !assignedTeamName) {
    return (
      <div data-testid="ticket-follow-up-assignment">
        <AssignmentRow
          className="text-muted-foreground"
          icon={
            <Avatar size="sm">
              <AvatarFallback>
                <UsersRoundIcon aria-hidden="true" size={12} />
              </AvatarFallback>
            </Avatar>
          }
        >
          {t("tickets.assignment.none")}
        </AssignmentRow>
      </div>
    );
  }

  return (
    <div className="flex min-w-0 flex-col gap-2 py-0.5" data-testid="ticket-follow-up-assignment">
      <AssignmentRow
        className={cn(!assignedTeamName && "text-muted-foreground")}
        icon={
          <Avatar size="sm">
            <AvatarFallback>
              <UsersRoundIcon aria-hidden="true" size={12} />
            </AvatarFallback>
          </Avatar>
        }
        title={assignedTeamName ?? undefined}
      >
        {assignedTeamName ?? t("tickets.assignment.noTeam")}
      </AssignmentRow>

      {assignee ? (
        <AssignmentRow
          icon={
            <Avatar size="sm">
              {assignee.avatarUrl ? <AvatarImage alt="" src={assignee.avatarUrl} /> : null}
              <AvatarFallback>{initialsOf(assignee.displayName)}</AvatarFallback>
            </Avatar>
          }
          title={assignee.displayName}
        >
          {assignee.displayName}
        </AssignmentRow>
      ) : (
        <AssignmentRow className="text-muted-foreground" icon={<UserRoundPlusIcon aria-hidden="true" />}>
          {t("tickets.assignment.pendingAssignee")}
        </AssignmentRow>
      )}
    </div>
  );
}

type AssignmentRowProps = {
  children: ReactNode;
  className?: string;
  icon: ReactNode;
  title?: string;
};

function AssignmentRow({ children, className, icon, title }: AssignmentRowProps) {
  return (
    <div className={cn("flex min-w-0 items-center gap-2.5 text-sm leading-5", className)}>
      <span className="flex size-6 shrink-0 items-center justify-center text-muted-foreground [&>svg]:size-4">{icon}</span>
      <span className="truncate text-sm font-normal" title={title}>
        {children}
      </span>
    </div>
  );
}
